import type { ColDef, GridApi } from "ag-grid-community";
import type { ReportPdfAlign, ReportPdfTable } from "@/shared/models/ReportPdf";

// The house grid theme centres every cell with `text-align: center !important`, so a
// column's own cellStyle says little about what the screen shows. A rendered cell does.
const renderedAlign = (colId: string): ReportPdfAlign | null => {
	const cell = document.querySelector(
		`.ag-cell[col-id="${CSS.escape(colId)}"]`,
	);
	if (!cell) return null;
	const align = getComputedStyle(cell).textAlign;
	if (align === "center") return "center";
	return align === "right" || align === "end" ? "right" : "left";
};

const declaredAlign = (colDef: ColDef): ReportPdfAlign => {
	const style = typeof colDef.cellStyle === "object" ? colDef.cellStyle : null;
	const align = style?.textAlign ?? style?.justifyContent;
	if (align === "center" || align === "right") return align;
	const types = [colDef.type ?? []].flat();
	return types.includes("rightAligned") || types.includes("numericColumn")
		? "right"
		: "left";
};

// The screen collapses line breaks and runs of spaces; the page prints what it shows.
const cellText = (value: unknown): string =>
	value == null ? "" : String(value).replace(/\s+/g, " ").trim();

// The RollShop prints size each column to what it holds: a code column as wide as its
// longest value or header word (headers wrap), a description wide enough for one line.
// Screen widths do not carry over, since a wide monitor stretches them. Measured in
// characters, with a cap so long notes wrap instead of taking the page.
const MAX_WIDTH_CHARS = 45;
const contentWidth = (header: string, cells: string[]): number => {
	const longestWord = header
		.split(" ")
		.reduce((max, word) => Math.max(max, word.length), 0);
	const longestCell = cells.reduce(
		(max, cell) => Math.max(max, cell.length),
		0,
	);
	return Math.min(Math.max(longestWord, longestCell, 3), MAX_WIDTH_CHARS) + 2;
};

/** A money total as the RollShop prints it: $40,342.05. */
export const formatMoneyTotal = (value: number): string =>
	`$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Sum of a column over the rows the grid shows, after filter. */
export const sumDisplayed = <T>(api: GridApi<T>, colId: string): number => {
	let sum = 0;
	api.forEachNodeAfterFilter((rowNode) => {
		if (!rowNode.group)
			sum += Number(api.getCellValue({ rowNode, colKey: colId })) || 0;
	});
	return sum;
};

interface Options {
	title?: string;
	emptyText?: string;
	/** Label of the row count printed under the table, as the print's `24  Total Rolls`. */
	countLabel?: string;
	/**
	 * Print alignment by column id. The screen centres every cell; the RollShop prints put
	 * descriptions and notes to the left and measures and money to the right.
	 */
	aligns?: Record<string, ReportPdfAlign>;
	/** Header alignment by column id; by default a left column has a left header, any other a centred one. */
	headerAligns?: Record<string, ReportPdfAlign>;
	/**
	 * Prints the rows in blocks, as the Rolls in Use print: a block opens when `key` changes,
	 * gets a rule above it, and the `repeat` columns print only on its first row.
	 */
	blocks?: { key: string; repeat: string[] };
	/** Money totals under their column, as the Partial Roll Set Rolls print's `$40,342.05  Total Value`. */
	sums?: { colId: string; label: string }[];
}

/**
 * The grid as the user sees it: the displayed columns in their order and width, and every
 * row after filter and sort, through each column's own formatter. A column drawn by a cell
 * renderer prints its raw value.
 */
export const gridToPdfTable = <T>(
	api: GridApi<T>,
	{
		title,
		emptyText,
		countLabel,
		aligns,
		headerAligns,
		blocks,
		sums,
	}: Options = {},
): ReportPdfTable => {
	const displayed = api.getAllDisplayedColumns();
	const rows: string[][] = [];
	const keys: unknown[] = [];
	api.forEachNodeAfterFilterAndSort((rowNode) => {
		if (rowNode.group) return;
		if (blocks) keys.push(api.getCellValue({ rowNode, colKey: blocks.key }));
		rows.push(
			displayed.map((column) => {
				const value = api.getCellValue({
					rowNode,
					colKey: column,
					useFormatter: true,
				});
				return cellText(value);
			}),
		);
	});

	const sumTotals = (sums ?? [])
		.map(({ colId, label }) => ({
			column: displayed.findIndex((c) => c.getColId() === colId),
			value: formatMoneyTotal(sumDisplayed(api, colId)),
			label,
		}))
		.filter((total) => total.column >= 0);

	const columns = displayed.map((column, index) => {
		const colId = column.getColId();
		const header = api.getDisplayNameForColumn(column, "header") ?? "";
		const align =
			aligns?.[colId] ??
			renderedAlign(colId) ??
			declaredAlign(column.getColDef());
		return {
			header,
			// A money total prints under its column, so it sizes it too.
			width: contentWidth(header, [
				...rows.map((row) => row[index]),
				...sumTotals.filter((t) => t.column === index).map((t) => t.value),
			]),
			align,
			headerAlign:
				headerAligns?.[colId] ?? (align === "left" ? "left" : "center"),
		};
	});

	const groupStarts: number[] = [];
	if (blocks) {
		const repeated = displayed
			.map((column, index) =>
				blocks.repeat.includes(column.getColId()) ? index : -1,
			)
			.filter((index) => index >= 0);
		keys.forEach((key, r) => {
			if (r === 0 || key !== keys[r - 1]) groupStarts.push(r);
			else for (const index of repeated) rows[r][index] = "";
		});
	}

	return {
		title,
		emptyText,
		rows,
		columns,
		groupStarts: blocks ? groupStarts : undefined,
		totals: [
			...(countLabel
				? [
						{
							count: true,
							column: 0,
							value: String(rows.length),
							label: countLabel,
						},
					]
				: []),
			...sumTotals,
		],
	};
};
